import React, { useState, useEffect, useContext } from "react";
import * as Sentry from "@sentry/react";
import { MacrosContext } from "../../../utils/MacrosContext";
import sendServerLogging from "../../../utils/sendServerLogging";
import { logInfo } from "../../../lib/sentryLogging";
import userJSON from "../../../utils/userJSON";
import { toast } from "react-toastify";
import AlertModal from "../../AlertModal";
import { getSessionToken } from "../../../lib/sessionToken";

export default function ManageMacrosPanel() {
  // Get macros data from context instead of props
  const { userMacros, setUserMacros, fetchUserMacros } =
    useContext(MacrosContext);

  // State to track which macros are expanded
  const [expandedMacros, setExpandedMacros] = useState(new Set());
  const [editedMacros, setEditedMacros] = useState({});
  const [isDeleting, setIsDeleting] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [macroToDelete, setMacroToDelete] = useState(null);

  // Initialize edited macros when userMacros changes
  useEffect(() => {
    const initialEdits = {};
    // Ensure userMacros is an array before using forEach
    if (Array.isArray(userMacros)) {
      userMacros.forEach((macro, index) => {
        initialEdits[index] = {
          keywords: Array.isArray(macro.keywords) ? macro.keywords : [],
          content: macro.content || "",
        };
      });
    }
    setEditedMacros(initialEdits);
  }, [userMacros]);

  useEffect(() => {
    setIsLoading(true);
    fetchUserMacros().finally(() => setIsLoading(false));
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchUserMacros();
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleMacro = (index) => {
    setExpandedMacros((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleFieldChange = (index, field, value) => {
    setEditedMacros((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]:
          field === "keywords"
            ? value
                .split(",")
                .map((k) => k.trim())
                .filter((k) => k)
            : value,
      },
    }));
  };

  const hasChanges = (macro, index) => {
    if (!editedMacros[index]) return false;
    const edited = editedMacros[index];
    const keywordsChanged =
      JSON.stringify(edited.keywords) !== JSON.stringify(macro.keywords);
    const contentChanged = edited.content !== macro.content;
    return keywordsChanged || contentChanged;
  };

  const handleSave = async (macro, index) => {
    const edited = editedMacros[index];

    if (!hasChanges(macro, index)) {
      return; // Nothing to save
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/macros/update-macro`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({
            dot_phrase: macro.dot_phrase,
            keywords: edited.keywords,
            content: edited.content,
          }),
        }
      );

      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "MACRO_UPDATED",
          eventData: {
            macro: macro.dot_phrase.toString(),
          },
        },
      });

      if (response.ok) {
        logInfo("Macro updated", "macro.updated");
        const updatedMacros = [...userMacros];
        updatedMacros[index] = {
          ...macro,
          keywords: edited.keywords,
          content: edited.content,
        };
        setUserMacros(updatedMacros);

        toast.success("Macro updated successfully!");
      } else {
        const error = await response.json();
        toast.error(`Failed to update macro: ${error.error}`);
        // Reset to original values
        setEditedMacros((prev) => ({
          ...prev,
          [index]: {
            keywords: macro.keywords,
            content: macro.content,
          },
        }));
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { logEvent: "ERROR_UPDATING_MACRO" },
      });
      toast.error("Error updating macro");
      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ERROR_UPDATING_MACRO",
          eventData: { error_message: err.message.toString() },
        },
      });
    }
  };

  const handleDelete = (macro, event) => {
    event.stopPropagation();
    setMacroToDelete(macro);
  };

  const confirmDelete = async () => {
    const macro = macroToDelete;
    setMacroToDelete(null);
    if (!macro) return;

    setIsDeleting(macro.dot_phrase);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/macros/delete-macro`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({
            dot_phrase: macro.dot_phrase,
          }),
        }
      );

      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "MACRO_DELETED",
          eventData: { macro: macro.dot_phrase.toString() },
        },
      });

      if (response.ok) {
        logInfo("Macro deleted", "macro.deleted");
        await fetchUserMacros();
        toast.success("Macro deleted successfully!");
      } else {
        // Try to parse as JSON first
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          toast.error(`Failed to delete macro: ${error.error}`);
        } else {
          // If not JSON, get the text to see what's being returned
          const text = await response.text();
          toast.error("Failed to delete macro: Server returned an error page");
        }
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { logEvent: "ERROR_DELETING_MACRO" },
      });
      toast.error("Error deleting macro");
      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ERROR_DELETING_MACRO",
          eventData: { error_message: err.message.toString() },
        },
      });
    } finally {
      setIsDeleting(null);
    }
  };

  // Ensure userMacros is an array
  const macros = Array.isArray(userMacros) ? userMacros : [];

  const filteredMacros = macros
    .map((macro, index) => ({ macro, index }))
    .filter(({ macro }) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const nameMatch = (macro.dot_phrase || "").toLowerCase().includes(query);
      const keywordMatch = Array.isArray(macro.keywords)
        ? macro.keywords.some((k) => k.toLowerCase().includes(query))
        : (macro.keywords || "").toLowerCase().includes(query);
      return nameMatch || keywordMatch;
    });

  return (
    <div id="manage_dot_phrase" className="bg-white rounded-lg p-6 flex flex-col h-[600px]">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl text-purple-700 font-bold">My Dot Phrases</h2>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh macros"
          aria-label="Refresh macros"
        >
          <svg
            aria-hidden="true"
            className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
      <p className="text-gray-600 italic mb-6">
        View all your saved dot phrases below. Click on a dot phrase to expand
        and edit.
      </p>

      {!isLoading && macros.length > 0 && (
        <div className="relative mb-4">
          <svg
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            aria-label="Search dot phrases"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by dot phrase name or keyword..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
          />
        </div>
      )}

      {isLoading ? (
        <div role="status" className="flex-1 flex flex-col items-center justify-center gap-3">
          <svg
            aria-hidden="true"
            className="w-10 h-10 text-purple-500 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <p className="text-gray-500 text-sm">Loading dot phrases...</p>
        </div>
      ) : macros.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">No dot phrases found.</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto pr-2">
          {filteredMacros.length === 0 && searchQuery.trim() ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 text-sm">No dot phrases match your search.</p>
            </div>
          ) : (
          <div className="space-y-2">
            {filteredMacros.map(({ macro, index }) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg transition-all duration-200 hover:shadow-md relative"
              >
                {/* Delete button */}
                <button
                  onClick={(e) => handleDelete(macro, e)}
                  className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-500 transition-colors z-10"
                  disabled={isDeleting === macro.dot_phrase}
                  aria-label={`Delete macro ${macro.dot_phrase}`}
                >
                  <svg
                    aria-hidden="true"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={() => toggleMacro(index)}
                  aria-expanded={expandedMacros.has(index)}
                  aria-controls={
                    expandedMacros.has(index) ? `manage-macro-panel-${index}` : undefined
                  }
                  className="w-full px-4 py-3 flex items-center text-left hover:bg-gray-50 transition-colors pr-12"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      aria-hidden="true"
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                        expandedMacros.has(index) ? "rotate-90" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-purple-600">
                      {macro.dot_phrase}
                    </h3>
                  </div>
                </button>

                {expandedMacros.has(index) && editedMacros[index] && (
                  <div
                    id={`manage-macro-panel-${index}`}
                    className="px-4 pb-4 border-t border-gray-100"
                  >
                    <div className="mt-3 space-y-3">
                      <div>
                        <label
                          htmlFor={`manage-macro-keywords-${index}`}
                          className="text-sm font-medium text-gray-700 block mb-1"
                        >
                          Keywords (comma-separated):
                        </label>
                        <input
                          id={`manage-macro-keywords-${index}`}
                          type="text"
                          value={editedMacros[index].keywords.join(", ")}
                          onChange={(e) =>
                            handleFieldChange(index, "keywords", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          placeholder="keyword1, keyword2, keyword3"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`manage-macro-content-${index}`}
                          className="text-sm font-medium text-gray-700 block mb-1"
                        >
                          Content:
                        </label>
                        <textarea
                          id={`manage-macro-content-${index}`}
                          value={editedMacros[index].content}
                          onChange={(e) =>
                            handleFieldChange(index, "content", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          rows="4"
                          placeholder="Macro content..."
                        />
                      </div>
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={() => handleSave(macro, index)}
                          disabled={!hasChanges(macro, index)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            hasChanges(macro, index)
                              ? "bg-purple-600 text-white hover:bg-purple-700"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {macroToDelete && (
        <AlertModal
          title="Confirm Delete"
          message={`Are you sure you want to delete the macro "${macroToDelete.dot_phrase}"?`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          showCancel
          onConfirm={confirmDelete}
          onClose={() => setMacroToDelete(null)}
        />
      )}
    </div>
  );
}
